'use client';

import type { SearchClient } from 'algoliasearch';
import { type LegacySearchMethodProps, liteClient } from 'algoliasearch/lite';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { InstantSearch, type InstantSearchProps } from 'react-instantsearch';

import { usePageContext } from '@commons/contexts/page';
import type { LOCALES } from '@commons/i18n';
import {
  ALGOLIA_API_KEY,
  ALGOLIA_APPLICATION_ID,
  ALGOLIA_INDEX,
  API_WEBSITE,
} from '@commons/utils/constants';

// Initialize Algolia search client.

const algoliaClient = liteClient(ALGOLIA_APPLICATION_ID, ALGOLIA_API_KEY);

// Timeout in milliseconds for fetch requests to the initial search cache endpoint.

const FETCH_TIMEOUT_MS = 5000;

// Default API route for fetching cached initial search results.

const DEFAULT_INITIAL_SEARCH_URL = '/api/search/initial';

// Minimal type for a search request's parameters.

type SearchRequestParams = {
  params?: {
    query?: string;
  };
};

/**
 * Check if at least one search request contains a non-empty query.
 *
 * @param requests - Array of search requests.
 *
 * @returns `true` if at least one request has a non-empty trimmed query.
 */

function hasNonEmptyQuery(requests: SearchRequestParams[]): boolean {
  return requests.some(({ params }) => !!params?.query?.trim());
}

/**
 * Create a fresh empty search result matching Algolia's response structure.
 *
 * Used as a placeholder when empty queries are prevented.
 *
 * @returns A new empty result object.
 */

function createEmptySearchResult() {
  return {
    hits: [],
    nbHits: 0,
    nbPages: 0,
    page: 0,
    processingTimeMS: 0,
    hitsPerPage: 0,
    exhaustiveNbHits: false,
    query: '',
    params: '',
  } as const;
}

// Type definitions for the shared InstantSearch provider properties.

export type InstantSearchProviderProps = Partial<InstantSearchProps> & {
  children: ReactNode;

  /**
   * Whether to proxy empty queries through the cache endpoint.
   * When `false`, empty queries return an empty placeholder result without any fetch.
   *
   * @default false
   */
  enableEmptySearch?: boolean;

  /**
   * URL of the POST route handler that returns cached initial search results.
   *
   * @default '/api/search/initial'
   */
  initialSearchUrl?: string;
};

/**
 * Shared InstantSearch provider component.
 *
 * Overrides the default search method to intercept empty queries:
 * - When `enableEmptySearch` is `false` (default), empty queries return a placeholder result
 *   without hitting Algolia, reducing API usage on pages where the search UI is rendered but idle.
 * - When `enableEmptySearch` is `true`, empty queries are proxied through `initialSearchUrl`
 *   which returns server-cached results, avoiding direct Algolia calls on every page load.
 *
 * @param props - Component props including InstantSearch configuration overrides.
 *
 * @see https://www.algolia.com/doc/api-reference/widgets/instantsearch/react/
 * @see https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-requests/react/
 */

export const InstantSearchProvider = ({
  children,
  enableEmptySearch = false,
  initialSearchUrl = DEFAULT_INITIAL_SEARCH_URL,
  routing = true,
  ...props
}: InstantSearchProviderProps) => {
  const { isEditing, isPreviewing } = usePageContext();

  /**
   * Custom search handler that intercepts empty queries before they reach Algolia.
   *
   * @param searchParams - The search parameters from InstantSearch widgets.
   *
   * @returns The search results from Algolia or a cached/empty fallback.
   */

  const handleSearch = useCallback<SearchClient['search']>(
    async (searchParams) => {
      const requests = searchParams as LegacySearchMethodProps;

      if (hasNonEmptyQuery(requests)) {
        return algoliaClient.search(requests);
      }

      if (!enableEmptySearch) {
        return { results: requests.map(() => createEmptySearchResult()) };
      }

      // Fetch cached initial results from the route handler.
      try {
        const response = await fetch(initialSearchUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ requests }),
          signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
        });

        if (!response.ok) {
          console.warn(
            `Failed to fetch initial search results (${response.status}), falling back to empty results.`
          );
          return { results: requests.map(() => createEmptySearchResult()) };
        }

        return response.json();
      } catch (error) {
        console.warn('Initial search request failed, falling back to empty results.', error);
        return { results: requests.map(() => createEmptySearchResult()) };
      }
    },
    [enableEmptySearch, initialSearchUrl]
  );

  return (
    <div id="search-wrapper" className="w-100">
      <InstantSearch
        future={{ preserveSharedStateOnUnmount: true }} // Upcoming version of InstantSearch will set this by default.
        routing={isEditing || isPreviewing ? false : routing}
        indexName={ALGOLIA_INDEX}
        searchClient={{ ...algoliaClient, search: handleSearch }}
        {...props}
      >
        {children}
      </InstantSearch>
    </div>
  );
};

/**
 * Helper function to get the default filters for a search query on Garda.com.
 *
 * @param locale - The current locale.
 *
 * @returns The default filters for the search query.
 */

export const getDefaultFilters = (locale: LOCALES) => {
  return `website_slug:${API_WEBSITE} AND langcode:${locale} AND status:true AND has_alias:true`;
};
